import { z } from "zod";
import { PRIVACY_NOTICE_VERSION } from "@tabbio/utils/analytics/consent";
import {
  SESSION_MAX_MS,
  uuidV7Timestamp,
} from "@tabbio/utils/analytics/browser-session";
export const MEASUREMENT_CONTEXT_HEADER = "x-tabbio-measurement-context";
export const MEASUREMENT_CONTEXT_MAX_AGE_MS = 30 * 60 * 1000;
export const measurementContextSchema = z
  .object({
    version: z.literal(1),
    noticeVersion: z.literal(PRIVACY_NOTICE_VERSION),
    capturedAt: z.string().datetime(),
    surface: z.enum(["web", "app_web"]),
    gaClientId: z
      .string()
      .regex(/^\d{1,20}\.\d{1,20}$/)
      .optional(),
    gaSessionId: z
      .string()
      .regex(/^\d{9,13}$/)
      .optional(),
    postHogSessionId: z.string().uuid().optional(),
    trafficType: z.enum(["internal", "test"]).optional(),
  })
  .strict();
export type BrowserMeasurementContext = z.infer<
  typeof measurementContextSchema
>;

/** Invalid or missing optional context never breaks a product request. */
export function parseMeasurementContext(
  raw: string | null | undefined,
  now = Date.now(),
): BrowserMeasurementContext | null {
  if (!raw || raw.length > 1024) return null;
  try {
    const parsed = measurementContextSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    const context = parsed.data;
    const time = Date.parse(context.capturedAt);
    if (time > now + 5000 || now - time > MEASUREMENT_CONTEXT_MAX_AGE_MS)
      return null;
    if (!context.gaClientId && !context.postHogSessionId) return null;
    if (context.gaSessionId) {
      const startedAt = Number(context.gaSessionId) * 1000;
      if (
        !context.gaClientId ||
        startedAt > time ||
        time - startedAt >= SESSION_MAX_MS
      )
        return null;
    }
    if (context.postHogSessionId) {
      const startedAt = uuidV7Timestamp(context.postHogSessionId);
      if (
        startedAt === null ||
        startedAt > time ||
        time - startedAt >= SESSION_MAX_MS
      )
        return null;
    }
    return context;
  } catch {
    return null;
  }
}
